import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { SnapCarousel } from '@/components/SnapCarousel';
import { TeamLogo } from '@/components/TeamLogo';
import SeriesCard from '@/components/SeriesCard';
import { RevealHighlight } from '@/components/RevealHighlight';

const NEW_WINDOW_MS = 48 * 60 * 60 * 1000;

function isFreshlyCreated(createdAt: string | null | undefined): boolean {
  if (!createdAt) return false;
  const age = Date.now() - new Date(createdAt).getTime();
  return age >= 0 && age <= NEW_WINDOW_MS;
}

interface SeriesCarouselProps {
  seriesList: any[];
  groupId: string;
  userBets: any[];
  seriesIdsWithOdds: Set<string>;
  onRevealClick?: (seriesId: string) => void;
}

// Chronological round order. Used to sort within a status tier so that
// newer-round series appear above older-round series during round overlap.
const ROUND_ORDER = ['first_round', 'round1', 'west_semis', 'east_semis', 'conf_semis', 'west_finals', 'east_finals', 'conf_finals', 'finals', 'champion'];

const STATUS_PRIORITY: Record<string, number> = { upcoming: 0, in_progress: 1, completed: 2 };

export function getOpenSeries(seriesList: any[], seriesIdsWithOdds: Set<string>): any[] {
  // Open = bettable (upcoming with odds pushed) OR still playing (in_progress).
  // Span rounds: next-round bettable series come first, then any previous-round
  // in_progress series that haven't finished yet. Keeps lingering games visible
  // while the next round opens for betting, instead of falling into a dead zone.
  const activeSeries = seriesList.filter((s) => {
    if (s.status === 'in_progress') return true;
    if (s.status === 'upcoming') return seriesIdsWithOdds.has(s.id);
    return false;
  });
  if (activeSeries.length === 0) return [];

  const roundIndex = (round: string) => {
    const i = ROUND_ORDER.indexOf(round);
    return i === -1 ? -1 : i;
  };

  return [...activeSeries].sort((a, b) => {
    // Tier 1: upcoming (bettable) before in_progress
    const sa = STATUS_PRIORITY[a.status] ?? 99;
    const sb = STATUS_PRIORITY[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    // Tier 2 (within same status): newer round first
    return roundIndex(b.round) - roundIndex(a.round);
  });
}

function useIsDesktop(breakpoint = 1024) {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isDesktop;
}

export function SeriesCarousel({ seriesList, groupId, userBets, seriesIdsWithOdds, onRevealClick }: SeriesCarouselProps) {
  const openSeries = getOpenSeries(seriesList, seriesIdsWithOdds);
  const isDesktop = useIsDesktop();

  // Distinguish "everything finished" vs "next round seeded but odds not pushed yet"
  const hasHiddenUpcoming = seriesList.some(
    (s) => s.status === 'upcoming' && !seriesIdsWithOdds.has(s.id)
  );
  const emptyMessage = hasHiddenUpcoming
    ? 'הסדרות לסיבוב הבא ייפתחו בקרוב'
    : 'כל הסדרות הסתיימו';

  const getBetForSeries = (seriesId: string) =>
    userBets.find((b) => b.series_id === seriesId);

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold tracking-tight">סדרות פתוחות</h2>
        <Link
          to={`/group/${groupId}/series`}
          className="text-sm text-accent hover:underline"
        >
          כל הסדרות ←
        </Link>
      </div>

      {openSeries.length === 0 ? (
        <div
          className={`rounded-2xl p-6 text-center ${
            hasHiddenUpcoming
              ? 'bg-card border border-accent/30 ring-1 ring-accent/15 shadow-[0_0_18px_rgba(255,140,40,0.10),inset_0_0_12px_rgba(255,140,40,0.04)]'
              : 'bg-card border border-border'
          }`}
        >
          <span className={`text-sm ${hasHiddenUpcoming ? 'text-accent font-medium' : 'text-muted-foreground'}`}>
            {emptyMessage}
          </span>
        </div>
      ) : isDesktop ? (
        /* Desktop: vertical list like the full series drill-in */
        <div className="space-y-0">
          {openSeries.map((series, idx) => (
            <SeriesCard
              key={series.id}
              series={series}
              groupId={groupId}
              userBet={getBetForSeries(series.id)}
              index={idx}
              onRevealClick={onRevealClick}
            />
          ))}
        </div>
      ) : (
        /* Mobile: horizontal snap carousel */
        <SnapCarousel cardWidth={140} cardWidthDesktop={200} gap={6} peekAmount={20}>
          {openSeries.map((series) => {
            const bet = getBetForSeries(series.id);
            // Format prediction as "Winner in N" from score like "Knicks 4-2" → 4+2=6
            const predictionLabel = bet?.predicted_winner && bet?.predicted_score
              ? (() => {
                  const match = bet.predicted_score.match(/(\d+)-(\d+)/);
                  if (!match) return bet.predicted_winner;
                  return `${bet.predicted_winner} in ${Number(match[1]) + Number(match[2])}`;
                })()
              : null;
            const isLocked = series.status === 'in_progress' || series.status === 'completed';
            const canRevealHere = isLocked && !!onRevealClick;
            const isUpcoming = series.status === 'upcoming';
            const isPastDeadline = series.betting_deadline && new Date(series.betting_deadline) < new Date();
            const isNew = isUpcoming && !isPastDeadline && !bet && isFreshlyCreated(series.created_at);
            const cardInner = (
              <div className={`relative rounded-2xl p-1.5 h-[120px] flex flex-col items-center justify-center gap-1 overflow-hidden ${
                  canRevealHere
                    ? 'bg-card border border-violet-400/40 shadow-[inset_0_0_14px_rgba(167,139,250,0.12)]'
                    : isNew
                      ? 'bg-card border border-accent/60 ring-1 ring-accent/30 shadow-[0_0_14px_rgba(255,140,40,0.18),inset_0_0_10px_rgba(255,140,40,0.05)]'
                      : !predictionLabel
                        ? 'bg-card/80 border border-border/60'
                        : 'bg-card border border-border'
                }`}>
                {canRevealHere && <RevealHighlight variant="compact" />}
                {isNew && (
                  <div className="absolute top-1 right-1 z-[2] flex items-center gap-0.5 bg-accent text-white text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shadow-[0_0_8px_rgba(255,140,40,0.5)] animate-pulse">
                    <Sparkles className="w-2.5 h-2.5" />
                    חדש
                  </div>
                )}
                <div className={`flex items-center gap-1 ${canRevealHere ? 'mt-4' : ''}`}>
                  <TeamLogo teamName={series.team_home} size={28} />
                  <span className="text-[10px] text-muted-foreground">vs</span>
                  <TeamLogo teamName={series.team_away} size={28} />
                </div>
                <div>
                  {predictionLabel ? (
                    <span className="bg-green-500/10 text-green-400 text-[10px] px-2 py-0.5 rounded-full">
                      {predictionLabel}
                    </span>
                  ) : isLocked ? (
                    <span className="bg-muted/30 text-muted-foreground text-[10px] px-2 py-0.5 rounded-full">
                      לא הימרת
                    </span>
                  ) : (
                    <span className="border border-accent text-accent text-[10px] px-2 py-0.5 rounded-full">
                      ממתין להימור
                    </span>
                  )}
                </div>
              </div>
            );
            if (isLocked && onRevealClick) {
              return (
                <button
                  key={series.id}
                  type="button"
                  onClick={() => onRevealClick(series.id)}
                  className="block w-full"
                >
                  {cardInner}
                </button>
              );
            }
            return (
              <Link
                key={series.id}
                to={`/group/${groupId}/series/${series.id}`}
                className="block"
              >
                {cardInner}
              </Link>
            );
          })}
        </SnapCarousel>
      )}
    </div>
  );
}

export default SeriesCarousel;
